import moment from 'moment-timezone'
import NumericInput from 'react-numeric-input'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { Col, Row } from 'reactstrap'
import { InputGroup } from 'rsuite'
import { resolveBackorderDisclaimer } from '../../helpers/backorderText'
import { isPunchoutUiMode } from '../../util/punchout'
import { resolveUnitPrice, setMaxQuantityLoading, setCheckoutLoading } from '../Checkout/checkoutFunctions'
import MultiAddressSelection from '../Checkout/MultiAddressSelection'
import dollar_price from '../general/DollarPrice'
import placeholder from '../Shop/placeholder-image.jpg'
import BundleSelectionErrors, { bundleSelectionErrorsForCartItem } from './BundleSelectionErrors'
import { RenderLineItemExtraInfo, RenderCartItemInfo } from './index'

const CartItemCard = ({
  cartItem,
  maxQtyProductReached,
  isCheckout,
  mini,
  index,
  tooltipOpen,
  setTooltipOpen,
  setQuery,
  cartItemQuantities = {},
  setCartItemQuantities,
  setDeleteConfirmationItem,
  hideMultiAddressDisplay,
  groupDestinationAddress
}) => {
  const dispatch = useDispatch()

  // Colocate selectors - select only what's needed
  const currentLocation = useSelector(state => state.storeViews.current)
  const portal = useSelector(state => state.portal)
  const errors = useSelector(state => state.checkout.errors)
  const checkout = useSelector(state => state.checkout)
  const currentUser = useSelector(state => state.currentUser)
  const session = useSelector(state => state.session)
  const isPunchoutMode = isPunchoutUiMode({ session, currentLocation })
  const { priceData, cart_item_id, product_id, images, show_backorder_text } = cartItem
  const backorderDisclaimer = resolveBackorderDisclaimer(portal, cartItem.fulfilment_type)

  // Hide multi-address UI in shipping groups when showing group-specific destination
  const showMultiAddressSelection =
    portal.multiple_address_shipping && currentUser.id && !isPunchoutMode && !hideMultiAddressDisplay
  const thisCartItemQty = parseInt(cartItemQuantities[cart_item_id])
  const bundleSelectionErrors = bundleSelectionErrorsForCartItem(checkout.validationErrors, cartItem)

  // get cart item's base unit price, before applied credits
  const cartItemUnitPrice = resolveUnitPrice(priceData)
  const cartItemUnitSetupCharge = priceData['setup_charge'] ? priceData['setup_charge'] : 0

  // the total of user's coop credits that have been APPLIED to this item's subtotal
  const totalAppliedCoopCredits =
    priceData.appliedCredits && priceData.appliedCredits.coop ? priceData.appliedCredits.coop : 0

  const toggleArtworkPreview = key => {
    if (key) {
      setTooltipOpen({ ...tooltipOpen, [key]: !tooltipOpen[key] })
    }
  }

  const handleUpdateQuantity = e => {
    setMaxQuantityLoading(true)
    dispatch(setCheckoutLoading(true))
    const incrementDecrementValue = cartItem.enable_qty_increments ? parseInt(cartItem.qty_increments) : 1
    let newQty = thisCartItemQty
    const operator = typeof e === 'object' ? e.target.innerText : null
    if (operator === '-') {
      newQty -= incrementDecrementValue
    } else if (operator === '+') {
      newQty += incrementDecrementValue
    } else {
      // if user is typing in input field, e is the value
      newQty = parseInt(e)
    }

    // first set state for responsiveness
    setCartItemQuantities({ ...cartItemQuantities, [cart_item_id]: newQty })
    // then put query in queue
    setQuery({ cartItemId: cart_item_id, qty: newQty })
  }

  const lineItemHasErrors =
    (maxQtyProductReached && maxQtyProductReached[product_id] === true) ||
    (errors.stock && errors.stock.find(ci => ci.cart_item_id === cart_item_id)) ||
    (cartItem.max_quantity && !currentUser.id) ||
    bundleSelectionErrors.length > 0

  // FOR HEADER CART
  if (mini) {
    return (
      <tr
        key={index}
        className="cartItemRow"
        id={`cart-item-row-${cartItem.cart_item_id}`}
        style={
          errors.stock && errors.stock.find(ci => ci.cart_item_id === cartItem.cart_item_id)
            ? { background: 'rgba(255, 0, 0, 0.21)' }
            : {}
        }
      >
        {/* THUMBNAIL IMAGE */}
        <td className="cartItemThumbnail" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
          <img
            src={cartItem.images && cartItem.images.length > 0 ? cartItem.images[0].small : placeholder}
            alt="cart-item"
            width="100"
          />
        </td>

        {/* NAME AND INFO */}
        <td className="cartItemDetails">{RenderCartItemInfo(cartItem, mini)}</td>

        {/* QUANTITY */}
        <td
          className="lineItemQuantity"
          style={{ maxWidth: '100px', minWidth: '90px', textAlign: 'center', verticalAlign: 'middle' }}
        >
          <span>
            {mini ? 'x' : null}
            {cartItem.quantity}
          </span>
        </td>

        {/* LINE ITEM SUBTOTAL */}
        <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
          {!isCheckout || (isCheckout && checkout.creditsUsed.coopCredits)
            ? dollar_price(cartItemUnitPrice * cartItem.quantity + cartItemUnitSetupCharge - totalAppliedCoopCredits)
            : // on checkout page, only include credits if user has it checked
              dollar_price(cartItemUnitPrice * cartItem.quantity + cartItemUnitSetupCharge)}
        </td>
      </tr>
    )
  }
  // END FOR HEADER CART

  // Calculate line item total
  const lineItemTotal =
    !isCheckout || checkout.creditsUsed?.coopCredits
      ? cartItemUnitPrice * cartItem.quantity + cartItemUnitSetupCharge - totalAppliedCoopCredits
      : cartItemUnitPrice * cartItem.quantity + cartItemUnitSetupCharge

  // FOR CART PAGE
  return (
    <Row
      id={`cart-item-row-${cart_item_id}`}
      className="g-0 mf-p-3 white-background box-shadow mf-my-3 mf-mx-0"
      style={{ background: lineItemHasErrors ? 'rgba(255, 0, 0, 0.21)' : '' }}
    >
      {/* Image */}
      <Col xs="auto" className="pe-3">
        <Link to={`/product/${product_id}/${portal.id}/${currentLocation?.id}`}>
          <img
            src={images && images.length > 0 ? images[0].small : placeholder}
            alt="cart-item"
            className="mf-rounded mf-border"
            style={{ width: 128, height: 128, objectFit: 'cover', backgroundColor: '#f9fafb' }}
          />
        </Link>
      </Col>

      {/* Product Details */}
      <Col className="d-flex flex-column">
        {/* Product Name */}
        <Link to={`/product/${cartItem.product_id}/${portal.id}/${currentLocation?.id}`}>
          <h5 className="mf-mb-2 fw-medium" style={{ lineHeight: 1.25 }}>
            {cartItem.name}
          </h5>
        </Link>

        {/* Backorder Text */}
        {show_backorder_text && backorderDisclaimer && (
          <p className="small text-danger mf-mb-1">{backorderDisclaimer}</p>
        )}

        {/* Product Attributes / Extra Info */}
        <div className="mf-mb-2">
          {/* Digital Proofing Info */}
          {cartItem.is_digital_proofing && cartItem.digitalProofing?.[0]?.approved_date && (
            <p className="small text-muted mf-mb-1">
              <span className="fw-medium text-dark">Approved:</span>{' '}
              {moment.tz(cartItem.digitalProofing[0].approved_date, moment.tz.guess()).format('MMMM Do YYYY, h:mm a')}
            </p>
          )}
          {cartItem.digitalProofing && !cartItem.error && (
            <p className="small mf-mb-1">
              <a
                href={cartItem.digitalProofing?.[0]?.artwork?.pdf_preview?.url || '#'}
                target="_blank"
                rel="noreferrer"
              >
                View Proofing Preview
              </a>
            </p>
          )}

          {/* Template Info */}
          {cartItem.template &&
            RenderLineItemExtraInfo(
              cartItem.template.template_fields,
              'templateFields',
              cartItem.cart_item_id,
              mini,
              tooltipOpen,
              toggleArtworkPreview
            )}

          {/* Configurable Info */}
          {cartItem.flags?.is_configurable &&
            RenderLineItemExtraInfo(
              cartItem.variants,
              'configurable',
              cartItem.cart_item_id,
              mini,
              tooltipOpen,
              toggleArtworkPreview
            )}

          {/* Bundle Info */}
          {cartItem.is_bundle === true &&
            RenderLineItemExtraInfo(
              cartItem.bundle.products,
              'bundleProduct',
              cartItem.cart_item_id,
              mini,
              tooltipOpen,
              toggleArtworkPreview
            )}

          {/* Artwork Info (Simple Product) */}
          {cartItem.flags?.is_artwork &&
            RenderLineItemExtraInfo(
              cartItem.artwork.nested_data,
              'artwork',
              cartItem.cart_item_id,
              mini,
              tooltipOpen,
              toggleArtworkPreview
            )}
        </div>

        {/* Production Time Warning */}
        {cartItem.fulfillment_production_time && (
          <div
            className="mf-rounded mf-border mf-py-2 mf-px-3 mf-my-3"
            style={{ backgroundColor: '#fef3cd', borderColor: '#ffc107' }}
          >
            <span className="small" style={{ color: '#664d03' }}>
              <span style={{ fontWeight: 600 }}>Production Time:</span> {cartItem.fulfillment_production_time}
            </span>
          </div>
        )}

        {/* Setup Charge */}
        {cartItemUnitSetupCharge > 0 && (
          <p className="small text-muted mf-mb-2">
            Total Price Includes A {dollar_price(cartItemUnitSetupCharge)} Setup Charge
          </p>
        )}

        {/* Digital Delivery Notice */}
        {cartItem.shippable_type === 'Digital Delivery' && (
          <p className="small text-muted fst-italic mf-mb-2">Digital downloads will be sent to your email address.</p>
        )}

        {/* Multi-address selection (cart page only) */}
        {showMultiAddressSelection && !isCheckout && <MultiAddressSelection cartItem={cartItem} />}

        {/* Shipping Address Display */}
        {groupDestinationAddress && isCheckout && cartItem.shippable_type !== 'Digital Delivery' && (
          <p className="small mf-mb-0" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
            <span style={{ fontWeight: 600 }}>Shipping To:</span>{' '}
            {groupDestinationAddress.first_name || groupDestinationAddress.full_name}{' '}
            {groupDestinationAddress.last_name || ''} •{' '}
            {groupDestinationAddress.address_line_1 || groupDestinationAddress.address1}, {groupDestinationAddress.city}
            , {groupDestinationAddress.state} {groupDestinationAddress.zip_code || groupDestinationAddress.postal_code}
          </p>
        )}

        {/* Multi-address selection display (checkout) */}
        {!groupDestinationAddress &&
          showMultiAddressSelection &&
          isCheckout &&
          checkout.multiAddressSelection?.length > 0 &&
          cartItem.shippable_type !== 'Digital Delivery' &&
          checkout.multiAddressSelection
            .filter(sel => sel.cartItemId === cart_item_id)
            .map((selection, idx) => {
              const addr = selection.address
              const showQuantity = checkout.multiAddressSelection.filter(s => s.cartItemId === cart_item_id).length > 1
              return (
                <p key={idx} className="small mf-mb-0" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                  <span style={{ fontWeight: 600 }}>Shipping To:</span>{' '}
                  {showQuantity && `(Qty: ${selection.quantity?.value || selection.quantity}) `}
                  {addr?.first_name || addr?.full_name} {addr?.last_name || ''} •{' '}
                  {addr?.address_line_1 || addr?.address1}, {addr?.city}, {addr?.state}{' '}
                  {addr?.zip_code || addr?.postal_code}
                </p>
              )
            })}

        {/* Co-op Savings (if applicable) */}
        {cartItem.priceData.coop_enabled &&
          (!isCheckout || checkout.creditsUsed?.coopCredits) &&
          totalAppliedCoopCredits > 0 && (
            <p className="small text-success mf-mb-0 mf-mt-2">
              {portal.context_dictionary?.coop_abbreviation} Saved You: {dollar_price(totalAppliedCoopCredits)}
            </p>
          )}
      </Col>

      {/* Pricing */}
      <Col xs="12" lg="2" className="d-flex flex-column justify-content-between mf-mt-3 mt-md-0">
        <div className="d-flex flex-column align-items-end">
          {/* Unit Price */}
          {currentLocation.show_price && (
            <p className="small mf-mb-1">
              {dollar_price(cartItemUnitPrice)}/ea
              {cartItem.priceData.tierPriceText && (
                <span className="fst-italic d-block">{cartItem.priceData.tierPriceText}</span>
              )}
            </p>
          )}

          {/* Quantity */}
          {isCheckout ? (
            <p className="small mf-mb-0">
              <span className="fw-medium text-dark">QTY:</span> {cartItem.quantity}
            </p>
          ) : (
            <div className="mf-mb-2">
              <InputGroup>
                <NumericInput
                  onChange={handleUpdateQuantity}
                  min={
                    cartItem.minimum_qty_allowed_in_shopping_cart ||
                    (cartItem.enable_qty_increments ? cartItem.qty_increments : 1)
                  }
                  max={1000000000}
                  step={cartItem.enable_qty_increments ? cartItem.qty_increments : 1}
                  value={cartItemQuantities[cartItem.cart_item_id]}
                  precision={0}
                  mobile
                  className="qtyinputbox w-100"
                  strict
                  snap
                />
              </InputGroup>
            </div>
          )}

          {/* Delete Button */}
          {!isCheckout && (
            <button
              type="button"
              className="btn btn-link mf-p-0 small text-danger text-decoration-underline"
              onClick={() => setDeleteConfirmationItem(cartItem)}
            >
              Remove Item
            </button>
          )}

          {/* Error Messages */}
          {maxQtyProductReached?.[cartItem.product_id] && (
            <p className="small text-danger mf-mb-0 mf-mt-1">
              You have exceeded the maximum allowed qty for this product.
            </p>
          )}
          {cartItem.max_quantity && !currentUser.id && (
            <p className="small text-danger mf-mb-0 mf-mt-1">Please sign in to see your allotted quantity.</p>
          )}
          <BundleSelectionErrors errors={checkout.validationErrors} cartItem={cartItem} />
        </div>

        {/* Total Price */}
        <div className="d-flex flex-column align-items-end">
          <h4 className="fw-semibold mf-mb-0 mf-mt-3">{dollar_price(lineItemTotal)}</h4>
        </div>
      </Col>
    </Row>
  )
}

export default CartItemCard
